import React, { useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { FieldArray, Formik } from 'formik';
import { findLastIndex } from 'lodash';
import classNames from 'classnames';
import Minus from 'mdi-react/CloseIcon';
import Plus from 'mdi-react/PlusIcon';

import { useBookStatus } from 'modules/common/BookingModal/hooks/useBookStatus';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { Reservations, ReservCell } from 'entities/Reservations';
import { Checkbox, Input, MaskInput, Textarea } from 'modules/shared/Forms';
import TopBorderBtn from 'modules/shared/TopBorderBtn';
import { usePrepareBook } from 'modules/common/BookingModal/hooks/usePrepareBook';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import DateTimePicker from '../DateTimePicker';
import styles from './BookModal.module.scss';
import { deleteOrdersModal } from '../../../../common/BookingModal/actions';
import FormikPatchTouched from '../../../Forms/FormikPatchTouched';
import { RootState } from '../../../../../rootReducer';

type Props = {
  isShow: boolean;
  schema: any;
  mode: 'director' | 'manager' | 'ordering';
  bookValues: any;
  bookCell: ReservCell | null;
  ordersError: any;
  onCloseModal: () => void;
  onSubmit: (value: Reservations) => void;
  onDeleteBook: () => void;
  onDecline: () => void;
  formRef: any;
};

const MAX_LINES = 30;

const BookModal: React.FC<Props> = (props) => {
  const {
    isShow,
    bookValues,
    bookCell,
    formRef,
    mode,
    schema,
    ordersError,
    onSubmit,
    onDecline,
    onDeleteBook,
    onCloseModal,
  } = props;

  if (!isShow) return null;

  const initValue = usePrepareBook(bookValues, bookCell);
  const dispatch = useDispatch();
  // const [err, setErr] = useState(false);

  const { text, color, shortName } = useBookStatus(initValue.bookingStatus);
  const cellSetting = useSelector((state: RootState) => state.managerModuleReducer.reservationsReducer.cellSetting);

  const isOtherManager = shortName === 'otherManager';
  const isDeleted = shortName === 'declined' || shortName === 'deleted';
  const isManager = mode === 'manager';
  const isDirector = mode === 'director';
  const isOrdering = mode === 'ordering';
  const { comment, checkInDate, bookingDate } = initValue;

  const startBlockHours = new Date(cellSetting.nextDayBookingBanBeginTime).getHours();
  const startBlockMins = new Date(cellSetting.nextDayBookingBanBeginTime).getMinutes();

  const hours = new Date().getHours();
  const mins = new Date().getMinutes();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isPast = new Date() >= new Date(checkInDate);
  const isToday = format(new Date(bookingDate), 'dd.MM.yyyy') === format(new Date(), 'dd.MM.yyyy');
  const isTomorrow = format(new Date(bookingDate), 'dd.MM.yyyy') === format(tomorrow, 'dd.MM.yyyy');

  const disableByDirectorProperty =
    isManager &&
    initValue.id.trim().length !== 0 &&
    (isTomorrow || isToday) &&
    (hours > startBlockHours || (hours === startBlockHours && mins > startBlockMins));

  const btnSubmitTitle = isManager ? 'Зберегти' : 'Підтвердити';

  const onRemoveHandler = (values: any, index: number) => {
    values.orders.splice(index, 1);
    dispatch(deleteOrdersModal({ ...values }));
  };

  const [ordersLine, setOrdersLine] = useState<number>(0);

  return (
    <ModalWrapper
      show={isShow}
      onHide={onCloseModal}
      size="lg"
      className={styles.modalBook}
      headerBtn={
        isManager && !isPast && !isOtherManager && initValue?.id && initValue?.bookingStatus !== 6 ? (
          <TopBorderBtn icon="delete" className="mt-2" clickHandler={onDeleteBook} />
        ) : null
      }
    >
      <Formik
        // @ts-ignore
        innerRef={formRef}
        initialValues={initValue}
        enableReinitialize
        onSubmit={onSubmit}
        validateOnMount
        validateFormWithHighPriority
        validationSchema={schema}
      >
        {({ values, isValid, handleSubmit, handleChange, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit} className="px-3">
            <FormikPatchTouched />
            <div className={styles.bookModalTitle}>
              <div className="d-flex flex-row align-items-center">
                <h4>Бронювання:&nbsp;</h4>
                <h4 className={styles[color]}>{text}&nbsp;</h4>
                {comment?.length && isDeleted ? <h4 className={styles[color]}>({comment})</h4> : null}
              </div>
              <div className={styles.dateRow}>
                <div className={styles.dateInput}>
                  <DateTimePicker
                    name="bookingDate"
                    isDisabled={
                      isPast ||
                      isOrdering ||
                      isDirector ||
                      isOtherManager ||
                      (isManager && values?.bookingStatus === 6) ||
                      isToday ||
                      disableByDirectorProperty
                    }
                  />
                  {isManager && (
                    <span className={styles.static}>Машина має приїхати за 20 хвилин до часу бронювання!</span>
                  )}
                </div>
              </div>
            </div>
            <Card.Body className="mt-4 px-0 pb-0 d-flex flex-row">
              <table className={styles.t}>
                <thead className={styles.tHeader}>
                  <th>. </th>
                  <th>Телефон</th>
                  <th>CМС сповіщення про статуси</th>
                </thead>
                <tbody className={styles.tBody}>
                  <tr key="customer" className={styles.tRow}>
                    <td className={styles.tCell}>Замовник:</td>
                    <td className={styles.inputCell}>
                      <MaskInput
                        name="contactPhone.number"
                        placeholder="38 (0XX) XXX-XX-XX"
                        onChange={handleChange}
                        disabled={isPast || isDirector || isOtherManager || isOrdering || disableByDirectorProperty}
                        isRequired
                        className={styles.inputSmallModal}
                        value={values.contactPhone?.number}
                      />
                    </td>
                    <td className={styles.checkCell}>
                      <Checkbox
                        name="sendCustomerMessage"
                        label=""
                        className={styles.checkbox}
                        onChange={() => setFieldValue('sendCustomerMessage', !values?.sendCustomerMessage)}
                        disabled={isPast || isDirector || isOtherManager || isOrdering || disableByDirectorProperty}
                        value={values.sendCustomerMessage}
                      />
                    </td>
                  </tr>
                  <tr key="manager" className={styles.tRow}>
                    <td className={styles.tCell}>Менеджер:</td>
                    <td className={styles.inputCell}>
                      <MaskInput
                        name="managerPhone.number"
                        placeholder="38 (0XX) XXX-XX-XX"
                        onChange={handleChange}
                        disabled={isPast || isDirector || isOtherManager || isOrdering || disableByDirectorProperty}
                        isRequired
                        className={styles.inputSmallModal}
                        value={values.managerPhone?.number}
                      />
                    </td>
                    <td className={styles.checkCell}>
                      <Checkbox
                        name="sendManagerMessage"
                        label=""
                        className={styles.checkbox}
                        onChange={() => setFieldValue('sendManagerMessage', !values?.sendManagerMessage)}
                        value={values.sendManagerMessage}
                        disabled={isPast || isDirector || isOtherManager || isOrdering || disableByDirectorProperty}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
            <Card.Body className={styles.ordersBlock}>
              <FieldArray
                name="orders"
                render={(arrayHelpers) => {
                  const addHandler = () => {
                    arrayHelpers.push({
                      reservationId: '',
                      orderParts: [],
                      orderObject: '',
                      precomplentation: false,
                      isCompleted: false,
                      isSmcCar: false,
                      company: {
                        name: '',
                        contactPerson: null,
                      },
                    });
                    setOrdersLine((prev) => prev + 1);
                  };

                  // @ts-ignore
                  return (
                    <Table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={classNames('d-flex align-items-center')}>
                            <Button
                              variant="success"
                              className="d-flex justify-content-center align-items-center mr-2 p-1 rounded-circle"
                              onClick={addHandler}
                              disabled={
                                isOtherManager ||
                                (ordersLine > MAX_LINES && isManager) ||
                                isDirector ||
                                isPast || // @ts-ignore
                                (values?.orders.length !== 0 && // @ts-ignore
                                  values?.orders[findLastIndex(values?.orders)]?.company?.iddp === undefined) ||
                                disableByDirectorProperty
                              }
                            >
                              <Plus />
                            </Button>
                            Замовлення
                          </th>
                          <th className={styles.companyNameCell}>Компанія</th>
                          <th className={styles.iddp}>Код діл-го партнера:</th>
                          <th className={styles.precomplectation}>Попередня комплектація</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.orders
                          ?.filter((v: any) => !v.isDeleted && !v.isInputError)
                          .map((v: any, index: number) => (
                            <tr key={index}>
                              <td className={classNames(styles.wideCell, 'pl-0')}>
                                <Input
                                  onChange={handleChange}
                                  label=""
                                  isRequired={isManager}
                                  className="w-100 ml-0 position-relative"
                                  type="string"
                                  requestParamName={isManager ? 'bookOrder' : 'modalInput'}
                                  maxLength={20}
                                  name={`orders[${index}].orderObject`}
                                  disabled={isPast || isDirector || isOtherManager || v?.company?.iddp}
                                  isDisabledBtnDots={
                                    isManager &&
                                    (isPast ||
                                      isOtherManager || // @ts-ignore
                                      (!!errors?.orders && !!errors?.orders[index]?.orderObject) || // @ts-ignore
                                      values?.orders[index]?.company?.iddp)
                                  }
                                  orderInfo={v}
                                >
                                  {!isPast && isManager && (
                                    <Button
                                      variant="danger"
                                      className={styles.removeBookBtn}
                                      onClick={() => {
                                        // @ts-ignore
                                        arrayHelpers.remove(index);
                                        setOrdersLine(ordersLine - 1);
                                        onRemoveHandler(values, index);
                                        // onRemoveOrder(values, index);
                                        // setErr(false);
                                      }}
                                    >
                                      <Minus size="14" />
                                    </Button>
                                  )}
                                  {ordersError && ordersError.orderObject === v.orderObject ? (
                                    <span className={styles.orderError}>{ordersError.error.message}</span>
                                  ) : null}
                                </Input>
                              </td>
                              <td>
                                <Textarea name={`orders[${index}].company.name`} label="" readOnly />
                              </td>
                              <td className={classNames(styles.iddp, 'position-relative')}>
                                <Textarea name={`orders[${index}].company.iddp`} label="" readOnly />
                                {v?.hasError && (
                                  <span className={styles.iddbError}>
                                    Замовлення в бронюванні мають бути на одну компанію
                                  </span>
                                )}
                              </td>
                              <td className={classNames(styles.shortCell, 'mt-2 d-flex justify-content-center')}>
                                <Checkbox
                                  name={`orders[${index}].precomplentation`}
                                  label=""
                                  onChange={handleChange}
                                  disabled={isPast || isManager || isDirector || isOrdering}
                                  readOnly
                                />
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  );
                }}
              />
            </Card.Body>
            <Card.Body className={styles.managerRow}>
              <h6 className="m-0">Менеджер: {values?.reservationOwner?.name}</h6>
            </Card.Body>
            {(!isOtherManager && !isPast && !isToday && isManager) ||
            (isDirector && !isToday && !isPast) ||
            isOrdering ? (
              <div className="d-flex justify-content-end">
                {isDirector && values?.bookingStatus !== 4 ? (
                  <Button
                    className={classNames(styles.btnSubmit, 'mr-2')}
                    disabled={!isValid}
                    variant="secondary"
                    onClick={onDecline}
                  >
                    <h5 className="text-white p-0 m-0">Відхилити</h5>
                  </Button>
                ) : null}
                {(isManager && values?.bookingStatus !== 6) ||
                (isDirector && values?.bookingStatus !== 2 && isDirector && values?.bookingStatus !== 4) ? (
                  <Button
                    className={styles.btnSubmit}
                    disabled={((!isValid || ordersError) && errors.orders !== '') || disableByDirectorProperty}
                    variant="danger"
                    type="submit"
                  >
                    <h5 className="text-white p-0 m-0">{btnSubmitTitle}</h5>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </form>
        )}
      </Formik>
    </ModalWrapper>
  );
};

export default BookModal;
